import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  UntypedFormControl,
  ValidationErrors,
} from '@angular/forms';
import { TripAttributes } from '~app/models';

export class FormsUtility {
  static requiredNotEmpty(control: AbstractControl): ValidationErrors | null {
    if (!control?.value || control.value.trim().length === 0) {
      return { requiredNotEmpty: 'This field is required.' };
    }

    return null;
  }

  static isIntegerOrNull(value: string | number) {
    if (!value) {
      return true;
    }
    if (Number.isNaN(Number(value))) {
      return false;
    }
    if (Number.parseInt(value as string).toString() === value.toString()) {
      return true;
    }
    return false;
  }

  static isIntegerValidator(control: UntypedFormControl) {
    if (FormsUtility.isIntegerOrNull(control.value)) {
      return null;
    } else {
      return { notInteger: `Number must be an integer.` };
    }
  }

  static isOverIntegerLimit(control: UntypedFormControl) {
    // Limit to 6 integers long
    let integerValue = control.value;

    if (integerValue > 999999 || integerValue < -999999) {
      return {
        charLimitExceeded: `Maximum number reached (Max 999999 characters actual ${integerValue})`,
      };
    }
  }

  static isOverCharLimit(control: UntypedFormControl) {
    // Limit 255 on backend
    // Using 250 for cosmetic reasons

    let fieldLength = control.value?.length;
    if (fieldLength > 250) {
      return {
        charLimitExceeded: `Maximum characters reached (Max 250 characters actual ${fieldLength})`,
      };
    }
  }

  static minAlphanumericChars(minChars: number = 2) {
    return (control: UntypedFormControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // Let required validator handle empty values
      }
      const value = String(control.value);
      // Count alphanumeric characters (letters and numbers)
      const alphanumericCount = (value.match(/[a-zA-Z0-9]/g) || []).length;
      if (alphanumericCount < minChars) {
        return {
          minAlphanumericChars: `Location name must contain at least ${minChars} alphanumeric character${minChars > 1 ? 's' : ''}`,
        };
      }
      return null;
    };
  }

  static endDateInPastValidator(control: UntypedFormControl) {
    let invalid = control.value < new Date().toISOString();
    if (invalid) {
      return {
        endDateInPast: 'Please add a future return date',
      };
    }
  }

  static endDateValidator(getStartDate: () => Date) {
    return (control: FormControl<Date>) => {
      let startDate = getStartDate();
      if (!startDate) {
        return;
      }
      let invalid = control?.value?.valueOf() < startDate.valueOf();
      if (invalid) {
        return {
          endDateBeforeStartDate: 'End Date must be after Start Date',
        };
      }
    };
  }

  static inBetweenStartAndEnd(
    getTrip: () => Pick<TripAttributes, 'startDate' | 'endDate'>
  ) {
    return (control: FormControl<Date>) => {
      let trip = getTrip();
      if (!trip) {
        return null;
      }
      let startDate = new Date(trip.startDate);
      let endDate = new Date(trip.endDate);
      let startError =
        new Date(control?.value)?.valueOf() < startDate.valueOf();
      let endError = new Date(control?.value)?.valueOf() > endDate.valueOf();
      const startErrorObj = {
        dateBeforeStartDate: 'Scheduled Date must be after Start Date',
      };
      const endErrorObj = {
        dateAfterEndDate: 'Scheduled Date must be before End Date',
      };
      if (startError && endError) {
        return { ...startErrorObj, ...endErrorObj };
      }
      if (startError) {
        return startErrorObj;
      }
      if (endError) {
        return endErrorObj;
      }
      return null;
    };
  }

  static combineChildValidation(control: AbstractControl): ValidationErrors {
    if (control instanceof FormArray) {
      let arr = control.controls.map((child) => child.errors);
      if (arr.every((item) => item === null)) {
        return null;
      } else {
        return arr;
      }
    }
    if (control instanceof FormGroup) {
      let arr = Object.entries(control.controls).map(([key, child]) => [
        key,
        child.errors,
      ]);
      if (arr.every((item) => item[1] === null)) {
        return null;
      } else {
        return Object.fromEntries(arr);
      }
    }
    return null;
  }
}
